import { select } from 'd3';
import type { HierarchyPointNode, HierarchyPointLink, HierarchyNode, Selection } from 'd3';
import type { TreeNode, TreeData } from '../types';
import $ from 'jquery';

// function to split CamelCase
/**
 * Splits a camelCase string into separate words with spaces.
 *
 * @param elem - The camelCase string to be split.
 * @returns The string with words separated by spaces.
 */
export function splitName(elem: string): string {
    const splitElem = elem
        .split(/(?=[A-Z])/)
        .map(function (p) {
            return p.charAt(0).toUpperCase() + p.slice(1);
        })
        .join(' ');
    return splitElem;
}

/**
 * Determine the coordinates of an svg element.
 *
 * @param selection - The D3 selection of SVG group elements to determine their bounding boxes.
 */
export function getBB(selection: Selection<SVGGElement, HierarchyPointNode<TreeNode>, SVGGElement, {}>) {
    selection.each(function (d: HierarchyPointNode<TreeNode>) {
        d.data.bbox = this.getBBox();
    });
}

/**
 * Traverses a tree structure and applies a function to each node.
 *
 * @param parent - The root node or data structure to start traversal from.
 * @param visitFn - The function to apply to each visited node.
 * @param childrenFn - The function to retrieve the children of a node.
 */
export function visit(
    parent: HierarchyPointNode<TreeNode> | TreeData | Function,
    visitFn: Function,
    childrenFn: Function
) {
    if (!parent) {
        return;
    }
    visitFn(parent);
    const children = childrenFn(parent);
    if (children) {
        const count = children.length;
        for (let i = 0; i < count; i++) {
            visit(children[i], visitFn, childrenFn);
        }
    }
}

/**
 * Counts the number of nodes at each level of the tree.
 *
 * @param levelWidth - An array where each index represents a tree level and its value represents the number of nodes at that level.
 * @param level - The current level in the tree being traversed.
 * @param n - The current node being processed in the tree.
 */
export function childCount(levelWidth: number[], level: number, n: HierarchyNode<TreeNode>) {
    if (n.children && n.children.length > 0) {
        if (levelWidth.length <= level + 1) {
            levelWidth.push(0);
        }
        levelWidth[level + 1] += n.children.length;
        n.children.forEach(function (d) {
            childCount(levelWidth, level + 1, d);
        });
    }
}

/**
 * Draws the links between the nodes.
 *
 * @param d - The link object containing source and target nodes.
 * @returns A string representing the SVG path for the link.
 */
export function diagonal(d: HierarchyPointLink<TreeNode>): string {
    let nodeGroup: SVGSVGElement,
        width = 0;
    if (d.source.data.id) {
        nodeGroup = select<SVGSVGElement, TreeNode>('#n-' + d.source.data.id).node();
    }
    // get width of node group element to set path accordingly
    if (nodeGroup) {
        width = nodeGroup.getBBox().width;
    }
    const path =
        'M' + (d.source.y + width + 20) + ',' + d.source.x + 'H' + (d.target.y - 15) + 'V' + d.target.x + ('h' + 15);
    return path;
}

/**
 * Animates a specific node by fading its text in and out.
 *
 * @param node - The node to be animated.
 */
export function animateNode(node: HierarchyPointNode<TreeNode>): void {
    for (let i = 0; i < 3; i++) {
        $('#n-' + node.data.id + ' .node-text')
            .fadeTo(400, 0)
            .fadeTo(400, 1);
    }
}
